import { Button, type DialogProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateZone } from '~/api/zones/update';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { LayoutList__Zone as Zone } from '../../queries/list.generated';
import { useZoneFormik } from '../lib';
import { ZoneForm, type ZoneFormValues } from './zone-form';

export interface EditZoneDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  open: boolean;
  zone: Zone;
}

export const EditZoneDialog = ({ close, open, zone }: EditZoneDialogProps) => {
  const { t } = useTranslation();
  const [updateZone] = useUpdateZone();

  const onSubmit = async (values: ZoneFormValues) => {
    const { errors } = await updateZone({
      refetchQueries: ['LayoutGet'],
      variables: { id: zone.id, patch: values },
    });
    if (!errors?.length) close();
  };

  const formik = useZoneFormik(
    {
      height: zone.height,
      left: zone.left,
      name: zone.name,
      top: zone.top,
      width: zone.width,
    },
    onSubmit,
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>
        {t('pages.layouts.detail.edit_zone_dialog.title', { zoneName: zone.name })}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.layouts.detail.edit_zone_dialog.description')}
        </DialogContentTitle>
        <ZoneForm formik={formik} id="edit-zone-form" />
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.actions.cancel')}
        </Button>
        <Button
          color="primary"
          form="edit-zone-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
